/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { RespondentAnswerTypes, ExternalQuestion } from '@sm/question-widgets/respondent-survey';
import Cookies from 'js-cookie';
import { AppDispatch, RootState } from '~app/store';
import {
  QuestionAnswers,
  SubmitMutation,
  CurrentSessionSurvey,
  CurrentSessionCollector,
  CurrentSessionCurrentPage,
  RespondentSession,
  StaticContextEnvironment,
} from './types';
import { SurveyFormat } from '~app/components/Survey/SurveyFormat/constants';
import { validate, deferToAfterFocusChanged } from './validation';
import { transformToSurveyErrors } from './errors/transformToSurveyErrors';
import { setErrorsById, removeErrorsById } from './errors/errorsSlice';
import { mapSurveyAnswersToResponse } from './helper/mapSurveyAnswersToResponse';
import { DEFAULT_SUBMIT_RESULT } from './constants';
import { RespApiAnswerInput } from '~lib/generatedGqlTypes';

// ========== INITIAL STATE
export type SurveyMode = typeof SurveyView[keyof typeof SurveyView];

export type SurveyState = {
  /** The survey object */
  survey?: CurrentSessionSurvey;
  /** The current page object */
  currentPage?: CurrentSessionCurrentPage;
  /** The collector object */
  collector?: CurrentSessionCollector;
  /** The client token */
  clientToken?: string;
  /** The answers for the current page */
  answers: QuestionAnswers;
  /** To track what view is shown to user */
  surveyView: SurveyMode;
  /* Redirect URL based off collector's Survey End Page feature */
  surveyEndPageUrl: string | null;
  /* Encrypted SMParams when instant results is enabled */
  encryptedInstantResultsSmParam: string | null;
  /* StaticContext Environment */
  environment?: StaticContextEnvironment;
};

/** Different views that are designed like a survey page but have different content */
export const SurveyView = {
  Taking: 'TAKING',
  ThankYou: 'THANK_YOU',
  Password: 'PASSWORD',
} as const;

export const initialState: SurveyState = {
  survey: undefined,
  currentPage: undefined,
  collector: undefined,
  clientToken: undefined,
  answers: {},
  surveyView: SurveyView.Taking,
  surveyEndPageUrl: '',
  encryptedInstantResultsSmParam: null,
  environment: undefined,
};

type SurveyCompleteAction = {
  smParamForSurveyComplete: string;
  reqLocale: string;
};

type SetCookieArgs = {
  name: string;
  value: string;
  expiration?: number | Date;
};

// ========== SLICE

const sliceName = 'survey';

export const surveySlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    initEnvironment: (state, action: PayloadAction<StaticContextEnvironment>) => {
      state.environment = action.payload;
    },
    initializeSurvey: (state, action: PayloadAction<RespondentSession>) => {
      state.survey = action.payload.survey;
      state.currentPage = action.payload.currentPage;
      state.collector = action.payload.collector;
      state.surveyEndPageUrl = action.payload.surveyEndPageUrl;
      state.encryptedInstantResultsSmParam = action.payload.encryptedInstantResultsSmParam;

      // initialize answers
      state.answers =
        action.payload.currentPage?.questionsQM?.items?.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: {
              questionId: curr.id,
              values: [],
              touched: false,
              isDirty: false,
            },
          }),
          {}
        ) ?? {};
    },
    setAnswers: (state, action: PayloadAction<QuestionAnswers>) => {
      state.answers = action.payload;
    },
    updateAnswers: (state, action: PayloadAction<QuestionAnswers>) => {
      state.answers = { ...state.answers, ...action.payload };
    },
    setClientToken: (state, action: PayloadAction<string>) => {
      state.clientToken = action.payload;
    },
    setSurveyView: (state, action: PayloadAction<SurveyMode>) => {
      state.surveyView = action.payload;
    },
    setCookie: (state, action: PayloadAction<SetCookieArgs>) => {
      const { environment: { domain, tld } = {} } = state;
      const { name, value, expiration } = action.payload;

      Cookies.set(name, value, {
        expires: expiration,
        domain: `.${domain}.${tld}`,
        secure: true,
        sameSite: 'None',
      });
    },
    surveyComplete: (state, action: PayloadAction<SurveyCompleteAction>) => {
      const isThankYouEnabled = state.collector?.thankYouPage?.isEnabled ?? false;
      const surveyResultsUrl = state.collector?.surveyResultsUrl ?? null;

      const { smParamForSurveyComplete, reqLocale } = action.payload;

      const { environment } = state;
      const isCurrentViewCustomThankYou = state.surveyView === 'THANK_YOU';

      const showQuizResultsPage = state.survey?.isQuiz && state.survey.quizOptions?.showResults;
      const showInstantResultsPage = !showQuizResultsPage && surveyResultsUrl;
      const showThankYouView = !showInstantResultsPage && isThankYouEnabled && !isCurrentViewCustomThankYou;
      const surveyEndPageRedirect = !showThankYouView;

      let redirectUrl: string | null = null;

      if (showQuizResultsPage) {
        if (surveyResultsUrl) {
          Cookies.set('sm_ir', state.encryptedInstantResultsSmParam ?? '', {
            domain: `.${environment?.domain}.${environment?.tld}`,
            secure: true,
            sameSite: 'None',
          });
        }

        redirectUrl = `/r/quiz/results?sm=${smParamForSurveyComplete}&lang=${reqLocale}`;
      } else if (showInstantResultsPage) {
        Cookies.set('sm_ir', state.encryptedInstantResultsSmParam ?? '', {
          domain: `.${environment?.domain}.${environment?.tld}`,
          secure: true,
          sameSite: 'None',
        });

        redirectUrl = surveyResultsUrl;
      } else if (showThankYouView) {
        state.surveyView = 'THANK_YOU';
      } else if (surveyEndPageRedirect) {
        redirectUrl = state.surveyEndPageUrl ?? null;
      }

      if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
    },
    reset: () => initialState,
  },
});

// ========== ACTIONS

export const {
  initializeSurvey,
  initEnvironment,
  setAnswers,
  updateAnswers,
  setClientToken,
  setSurveyView,
  setCookie,
  surveyComplete,
  reset,
} = surveySlice.actions;

// ========== THUNKS

/**
 * Update an answer - the specific answer is inferred by the `questionId` inside the answer object.
 * Error validation is also run for the new answer and the errors state is updated accordingly.
 *  */
export const updateAnswer = createAsyncThunk(`${sliceName}/updateAnswer`, (answer: RespondentAnswerTypes, thunkAPI) => {
  const { questionId } = answer;
  const { surveyState } = thunkAPI.getState() as RootState;
  const dispatch = thunkAPI.dispatch as AppDispatch;

  if (!answer.touched) {
    return; // no need for updates if answer was not touched
  }

  const { isValid, errors } = validate(questionId);
  deferToAfterFocusChanged(() => {
    // defer to make done with error-change click work
    if (!isValid) {
      const questionErrors = transformToSurveyErrors(errors);
      dispatch(setErrorsById({ id: questionId, newErrors: questionErrors }));
    } else {
      dispatch(removeErrorsById(questionId));
    }
  });

  dispatch(
    updateAnswers({
      [questionId]: { ...surveyState.answers[questionId], ...answer },
    })
  );
});

/**
 * Submit the survey response.
 * Currently requires the mutation function to be passed as a parameter as it is generated by hook.
 * Note: dateStarted and dateCompleted also need to be passed in as part of a solution to
 * fix failing integration tests involving mocked timers and promises.
 *  */
export const submitSurvey = createAsyncThunk(
  `${sliceName}/submit`,
  async (
    {
      mutation,
      dateStarted,
      dateCompleted,
      reqLocale,
    }: { mutation: SubmitMutation; dateStarted: string; dateCompleted: string; reqLocale: string },
    { getState }
  ) => {
    const { surveyState } = getState() as RootState;
    if (surveyState.survey && surveyState.collector) {
      try {
        // Only use the answered questions in payload (SPAGE-7410)
        const responses = mapSurveyAnswersToResponse({
          questions: (surveyState.currentPage?.questionsQM?.items ?? []) as ExternalQuestion[],
          answers: surveyState.answers,
          pageId: surveyState.currentPage?.id ?? '',
        }) as Required<RespApiAnswerInput[]>; // this typecast is necessary because gql InputMaybe types expect null values, which are not allowed in the response

        const { id: collectorId, isAnonymous, thankYouPage, surveyResultsUrl } = surveyState.collector;
        const {
          version,
          language: { id: surveyLangId },
          isQuiz,
          quizOptions,
        } = surveyState.survey;
        const generateQuizResultsSMParams = isQuiz && (quizOptions?.showResults ?? false);

        /**
         * Mutation calls SurveyResponses.graphql::submitSurveyResponse, which calls
         * @see https://code.corp.surveymonkey.com/webplatform/smweb/blob/master/api/graphapi/src/repos/responses/index.ts#L108
         * in turn
         * @see https://code.corp.surveymonkey.com/devmonkeys/ResponseWeb/blob/develop/responseweb/views/response.py#L167
         */
        const { data: { submitSurveyResponse } = {} } = await mutation({
          variables: {
            surveyId: surveyState.survey.id ?? '',
            input: {
              collectorId,
              isAnonymous: isAnonymous ?? false,
              surveyVersion: version ?? 1,
              dateStarted,
              dateCompleted,
              respondentId: '',
              responses,
              generateQuizResultsSMParams,
              surveyLangId,
              redirectUrl: surveyState.surveyEndPageUrl,
              reqLocale,
              isThankYouEnabled: thankYouPage?.isEnabled ?? false,
              instantResultsUrl: surveyResultsUrl ?? null,
            },
          },
        });

        return submitSurveyResponse;
      } catch (error: unknown) {
        // To be completed in server error validation story
        console.log(error);
      }
    }
    return DEFAULT_SUBMIT_RESULT;
  }
);

export const nextPage = createAsyncThunk(`${sliceName}/nextPage`, () => {
  console.log('Use this method to get the next page.  Pending decision about pagination');
});

export const previousPage = createAsyncThunk(`${sliceName}/previousPage`, () => {
  console.log('Use this method to get the previous page.  Pending decision about pagination');
});

// ========== SELECTORS

/** The survey state object */
const selectSurveyState = (state: RootState): SurveyState => state.surveyState;

/** The survey object */
export const selectSurvey = createSelector(selectSurveyState, surveyState => surveyState.survey);

/** The current page object */
export const selectCurrentPage = createSelector(selectSurveyState, surveyState => surveyState.currentPage);

/** The collector object */
export const selectCollector = createSelector(selectSurveyState, surveyState => surveyState.collector);

/** The client token */
export const selectClientToken = createSelector(selectSurveyState, surveyState => surveyState.clientToken);

export const selectSurveyView = createSelector(selectSurveyState, surveyState => surveyState.surveyView);

/** The survey format (eg. classic or OQAAT) */
export const selectSurveyFormat = createSelector(
  selectSurvey,
  survey => survey?.format ?? SurveyFormat.ONE_QUESTION_AT_A_TIME
);

/** Array containing all questions on the current page */
export const selectQuestions = createSelector(
  selectCurrentPage,
  currentPage => (currentPage?.questionsQM?.items ?? []) as ExternalQuestion[]
);

/** Object containing all answers for the current page */
export const selectAnswers = createSelector(selectSurveyState, surveyState => surveyState.answers);

/** The answer object for the question specified by Id */
export const selectAnswerById = createSelector(
  [selectAnswers, (_, questionId: string) => questionId],
  (answers, questionId) => answers[questionId]
);

/**
 * The total number of questions that have a value (i.e. answered), on the current page
 * note: currently only works with single-value questions
 * TODO: refactor to include answered questions on other pages once we get that data from the backend
 *  */
export const selectTotalAnswers = createSelector(selectAnswers, answers =>
  !answers ? 0 : Object.values(answers).reduce((acc: number, answer) => (answer.values?.[0]?.value ? acc + 1 : acc), 0)
);
/*
 * Static Context Environment
 */
export const selectEnvironment = createSelector(selectSurveyState, state => state.environment);

// ========== EXPORT

export default surveySlice.reducer;
